import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ReactTabulator } from 'react-tabulator';
import { confirmDialog } from 'primereact/confirmdialog';
import { Message } from 'primereact/message';
import { PanelMenu } from 'primereact/panelmenu';
import { deleteRequest, downloadBlobGet, fetchTextGet, getRequest, postRequest, tabulatorGet } from '../../api/ApiController';
import AuthContext from '../../components/AuthContext';
import { DatabaseContext } from '../../redux/DatabaseStore';
import { LocaleContext } from '../../components/LocaleContext';
import UtilContext from '../../components/UtilContext';
import PrintOptionsDialog from '../../components/PrintOptionsDialog';
import TabulatorMaskDialog from '../../utils/tabulatorMaskDialog';
import { buildExportQueryStringFromParams, openPrintHtmlWindow, triggerBlobDownload } from '../../utils/serviceTaskExport';
import { EXPORT_DOCUMENT_TITLE_KEYS, excelSheetTitleFromDocumentTitle, xlsxDownloadFilename } from '../../utils/exportExcelNaming';
import {
  applyUserMaskRowToTabulator,
  cleanSorters,
  createDedupedAjaxRequest,
  extractSafePaginationParams,
  mergePagedStandardListFiltersAndSorters,
  parseSorters,
  softRefreshProgressiveFromStart,
  safeParse,
  sanitizeTabulatorLayoutAttributes,
} from '../../utils/tabulatorProgressiveLoading';
import { applyCrmCommColumnVisibilitySafely } from '../../utils/crmCommColumnFieldAliases';
import {
  applyDrilldownOverallStatusPrecedence,
  hasDrilldownOverallStatusFilter,
} from '../../utils/crmSalesTurnoverUi';
import { getTabulatorPersistLayoutEvents } from '../../utils/tabulatorPersistLayout';
import { getTabulatorLayoutForStandardMask } from '../../utils/tabulatorLayoutUtils';
import { resetTabulatorMask } from '../../utils/tabulatorResetMask';
import { scrollToFirst as scrollToFirstUtil, scrollUp as scrollUpUtil, scrollDown as scrollDownUtil, scrollToLast as scrollToLastUtil } from '../../utils/tabulatorScroll';
import { getPusherChannel } from '../../utils/pusherChannel';
import {
  hasCrmModuleAccess,
  hasCrmModuleCompleteList,
  hasCrmModuleDelete,
  hasFpCrmAccess,
  hasFpCrmCompleteList,
  hasFpCrmDelete,
} from '../../utils/crmCommunicationPermissions';
import { loadCompaniesForTicketCatalog } from '../../utils/crmCatalogStore';
import { CRM_COMMUNICATION_TYPE_FILTER_VALUES, crmCommunicationTypeLabelKey } from '../../utils/crmCommunicationTypeIcons';
import {
  CRM_COMM_FROZEN_FIELDS,
  CRM_COMM_FULL_COLUMN_I18N_KEYS,
  CRM_COMM_MASK_FIELD_LABEL_KEYS,
  CRM_COMM_STATE_LIST_FROZEN_FIELDS,
  CRM_COMM_TICKETS_FROZEN_FIELDS,
  CRM_COMM_TICKETS_MASK_FIELD_LABEL_KEYS,
  CRM_COMM_TICKETS_PRINT_COLUMNS_ALLOWED,
  buildCrmCommunicationTableColumns,
  buildCrmCommunicationTabulatorOptions,
  createCrmCommDateColumnFilter,
  createCrmCommDateOrUserFilter,
  crmCommColumnMaskPage,
  getCrmCommEffectiveMask,
  getDefaultCrmCommColumnsVisible,
  mergeCrmCommColumnVisibility,
  CRM_COMM_PAGE_COMPLETE_LIST,
  CRM_COMM_PAGE_MY_AGENDA,
  CRM_COMM_PAGE_MY_BUSINESS_BY_STATE,
  CRM_COMM_PAGE_ALL_BUSINESS_BY_STATE,
} from '../../utils/crmCommunicationTableColumns';
import { applyTabulatorRuntimeI18n } from '../../utils/tabulatorRuntimeI18n';
import { buildCrmWorkflowStatusFilterOptions } from '../../utils/crmCommunicationTicketStatus';
import { getPreviewDateRange, PREVIEW_PERIOD_SEGMENTS } from '../../utils/previewDateRange';
import { getTimezone } from '../../utils/moduleSettings';
import {
  CRM_COMM_TICKETS_DEFAULT_STATUS,
  CRM_COMM_TICKETS_SCOPE_VALUES,
  CRM_COMM_TICKETS_STATUS_VALUES,
  mapCrmCommTicketsStatusToFilterStatus,
  normalizeCrmCommTicketsStatusFromMask,
  normalizeCrmCommTicketsScopeFromMask,
  applyCrmCommTicketsScopeToRequestExtra,
} from '../../utils/crmCommunicationTicketFilters';
import {
  appendCrmCommunicationTicketFilter,
  normalizeCrmTicketSorters,
} from '../../utils/crmCommunicationTicketRequestParams';
import CrmCommunicationListPageShell from './CrmCommunicationListPageShell';
import IntersectionObserverWrapper from '../../components/toolbar/IntersectionObserverWrap';
import {
  BackIcon,
  BackToFirstIcon,
  DeleteIcon,
  EditIcon,
  ExportIcon,
  FavoriteIcon,
  ForwardIcon,
  ForwardToLastIcon,
  PriceIcon,
  PrintIcon,
  RefreshIcon,
  VerticalLineIcon,
} from '../svg/svgIcons';
import 'react-tabulator/lib/styles.css';
import '../../assets/css/support.css';
import '../../assets/css/style.css';
import '../../assets/css/iconsmenu.css';

export const CRM_COMM_MASK_MY_AGENDA = 'cust019';
export const CRM_COMM_MASK_COMPLETE_LIST = 'cust021';
export const CRM_COMM_MASK_DETAIL = 'cust020';
export const CRM_COMM_MASK_PORTAL_DETAIL = 'intr014';
export { CRM_COMM_PAGE_MY_AGENDA, CRM_COMM_PAGE_COMPLETE_LIST } from '../../utils/crmCommunicationTableColumns';
export const CRM_COMM_PAGE_PORTAL = 'ServiceTaskList';

function applyDataColumnVisibilityToTable(table, visibilityMap, layoutMode = 'crm_full', stateListLayout = false) {
  if (!table) return;
  const map = mergeCrmCommColumnVisibility(visibilityMap, layoutMode, stateListLayout);
  const orderedFields = Object.keys(getDefaultCrmCommColumnsVisible(layoutMode, stateListLayout));
  // Legacy masks may reference removed/renamed fields (execution_crm_step_id, custom_delete).
  // Ignore unknowns — do not throw / do not force reload.
  applyCrmCommColumnVisibilitySafely(table, map, orderedFields);
  setTimeout(() => {
    table.redraw?.(true);
    table.modules?.layout?.layout?.();
  }, 0);
}

/**
 * @param {{
 *   mineOnlyDefault?: boolean,
 *   companyId?: number|null,
 *   types?: string|null,
 *   embedded?: boolean,
 *   embeddedFillParent?: boolean,
 *   listPageKey?: string,
 *   maskCode?: string,
 *   portalView?: boolean,
 *   crmTicketsLayout?: boolean,
 *   @deprecated serviceTaskLayout – alias pre crmTicketsLayout (portál v service/ bez úpravy)
 *   serviceTaskLayout?: boolean,
 *   preview?: string|null,
 *   timePeriod?: string|null,
 *   date?: Date|Date[]|null,
 *   scope?: string|null,
 *   traderBusinessScope?: boolean,
 *   filterTraderId?: number|null,
 *   stateListLayout?: boolean,
 *   defaultStatusValue?: string|null,
 * }} props
 */
function previewScopeToListScope(previewScope) {
  if (previewScope === 'moje_tikety') return CRM_COMM_TICKETS_SCOPE_VALUES.MINE;
  if (previewScope === 'moje_ulohy') return CRM_COMM_TICKETS_SCOPE_VALUES.EXECUTOR;
  return CRM_COMM_TICKETS_SCOPE_VALUES.ALL;
}

export default function CrmCommunicationTicketsList({
  mineOnlyDefault = true,
  companyId = null,
  types = null,
  embedded = false,
  embeddedFillParent = false,
  listPageKey: listPageKeyProp = null,
  maskCode: maskCodeProp = null,
  portalView = false,
  crmTicketsLayout: crmTicketsLayoutProp,
  serviceTaskLayout: serviceTaskLayoutLegacy = false,
  preview = null,
  timePeriod = null,
  date = null,
  scope = null,
  traderBusinessScope = false,
  filterTraderId = null,
  stateListLayout = false,
  defaultStatusValue = null,
  initialHeaderFilters = [],
  initialRequestFilters = {},
}) {
  const crmTicketsLayout = Boolean(crmTicketsLayoutProp ?? serviceTaskLayoutLegacy);
  const isBusinessByStateList = listPageKeyProp === CRM_COMM_PAGE_MY_BUSINESS_BY_STATE
    || listPageKeyProp === CRM_COMM_PAGE_ALL_BUSINESS_BY_STATE;
  const defaultListStatus = defaultStatusValue || (stateListLayout || isBusinessByStateList
    ? CRM_COMM_TICKETS_STATUS_VALUES.ALL
    : (crmTicketsLayout ? CRM_COMM_TICKETS_DEFAULT_STATUS : 'open'));
  const { t, locale } = useContext(LocaleContext);
  const { toast, openFavoriteDialog, setFastView, setUserMaskFilter, setStandardMaskFilter, deleteStandardMaskFilter, openChosenNotificationPreferences, getDateFormat, formatDate, getUserInitials, getUserColor, setDialogText, setVisible } = useContext(UtilContext);
  const { user } = useContext(AuthContext);
  const [state, dispatch] = useContext(DatabaseContext);
  const previewRef = useRef(preview);
  const timePeriodRef = useRef(timePeriod);
  const dateRef = useRef(date);
  const scopeRef = useRef(scope);
  const tableRef = useRef(null);
  const tableContainerRef = useRef(null);
  const overlayRef = useRef(null);
  const tableBuiltRef = useRef(false);
  const pendingPusherRefreshRef = useRef(false);
  const pendingRowIdsRef = useRef(new Set());
  const needsRedrawRef = useRef(false);
  const rafRedrawRef = useRef(null);
  const inFlightRef = useRef({ sig: null, promise: null });
  const completedCacheRef = useRef(new Map());
  const lastResponseRef = useRef(null);
  const isRestoringRef = useRef(false);
  const userInteractedRef = useRef(false);
  const savedHScrollRef = useRef({ left: 0, pending: false });
  const resetToFirstPageRef = useRef(false);
  const topFiltersMountedRef = useRef(false);
  const mineOnlyRef = useRef(!!mineOnlyDefault);
  const companyIdRef = useRef(companyId);
  const typesRef = useRef(types);
  const traderBusinessScopeRef = useRef(!!traderBusinessScope);
  const filterTraderIdRef = useRef(filterTraderId);
  const initialStatusValue = defaultListStatus;
  const initialScopeValue = crmTicketsLayout
    ? CRM_COMM_TICKETS_SCOPE_VALUES.ALL
    : (mineOnlyDefault ? CRM_COMM_TICKETS_SCOPE_VALUES.MINE : CRM_COMM_TICKETS_SCOPE_VALUES.ALL);
  const statusValueRef = useRef(initialStatusValue);
  const scopeValueRef = useRef(initialScopeValue);
  const typeValueRef = useRef('All');
  const [statusValue, setStatusValue] = useState(initialStatusValue);
  const [scopeValue, setScopeValue] = useState(initialScopeValue);
  const [typeValue, setTypeValue] = useState('All');
  const listPageKey = useMemo(() => {
    if (listPageKeyProp) return listPageKeyProp;
    if (portalView) return CRM_COMM_PAGE_PORTAL;
    return mineOnlyDefault ? CRM_COMM_PAGE_MY_AGENDA : CRM_COMM_PAGE_COMPLETE_LIST;
  }, [listPageKeyProp, portalView, mineOnlyDefault]);
  const maskCode = maskCodeProp || (mineOnlyDefault ? CRM_COMM_MASK_MY_AGENDA : CRM_COMM_MASK_COMPLETE_LIST);
  const columnMaskPage = useMemo(
    () => crmCommColumnMaskPage(listPageKey, crmTicketsLayout),
    [listPageKey, crmTicketsLayout],
  );
  const listTitle = useMemo(() => {
    if (portalView) return t('ServiceTaskList');
    if (listPageKey === CRM_COMM_PAGE_MY_BUSINESS_BY_STATE) return t('Menu.CRM.MyBusiness.ByState');
    if (listPageKey === CRM_COMM_PAGE_ALL_BUSINESS_BY_STATE) return t('Menu.CRM.AllBusiness.ByState');
    return mineOnlyDefault ? t('Menu.CRM.Communication.MyAgenda') : t('Menu.CRM.Communication.CompleteList');
  }, [listPageKey, mineOnlyDefault, portalView, t]);
  const favoritePageKey = useMemo(() => ({
    [CRM_COMM_PAGE_MY_AGENDA]: 'CrmCommunicationMyAgendaList',
    [CRM_COMM_PAGE_COMPLETE_LIST]: 'CrmCommunicationCompleteList',
  }[listPageKey] ?? listPageKey), [listPageKey]);
  const columnLayoutMode = useMemo(() => {
    if (crmTicketsLayout) return 'crm_tickets';
    if (portalView) return 'portal';
    return 'crm_full';
  }, [crmTicketsLayout, portalView]);
  const canDeleteInTable = !stateListLayout && crmTicketsLayout && (
    portalView
      ? hasFpCrmDelete(user, { mineOnly: mineOnlyDefault })
      : hasCrmModuleDelete(user, { mineOnly: mineOnlyDefault })
  );
  const canDeleteToolbar = stateListLayout && (
    portalView
      ? hasFpCrmDelete(user, { mineOnly: mineOnlyDefault })
      : hasCrmModuleDelete(user, { mineOnly: mineOnlyDefault })
  );
  const canEditToolbar = stateListLayout && hasCrmModuleAccess(user);
  const listFrozenFields = stateListLayout
    ? CRM_COMM_STATE_LIST_FROZEN_FIELDS
    : (columnLayoutMode === 'crm_tickets' ? CRM_COMM_TICKETS_FROZEN_FIELDS : CRM_COMM_FROZEN_FIELDS);
  const defaultVisible = useMemo(() => {
    const base = getDefaultCrmCommColumnsVisible(columnLayoutMode, stateListLayout);
    if (columnLayoutMode !== 'crm_tickets') return base;
    if (stateListLayout) {
      return { ...base, custom_delete: false };
    }
    return { ...base, custom_delete: !!canDeleteInTable };
  }, [canDeleteInTable, columnLayoutMode, stateListLayout]);
  const [columnsVisible, setColumnsVisible] = useState(() => {
    const base = getDefaultCrmCommColumnsVisible(columnLayoutMode, stateListLayout);
    if (columnLayoutMode !== 'crm_tickets') return base;
    if (stateListLayout) {
      return { ...base, custom_delete: false };
    }
    return { ...base, custom_delete: !!canDeleteInTable };
  });
  const [toolbarEditDisabled, setToolbarEditDisabled] = useState(true);
  const [toolbarDeleteDisabled, setToolbarDeleteDisabled] = useState(true);
  const [maskVisible, setMaskVisible] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState({});
  const [printChoiceVisible, setPrintChoiceVisible] = useState(false);
  const [printOrientationChoice, setPrintOrientationChoice] = useState('landscape');
  const [printLayoutChoice, setPrintLayoutChoice] = useState('branded');
  const canSeeCompleteList = portalView
    ? hasFpCrmCompleteList(user)
    : hasCrmModuleCompleteList(user);
  const lockMineOnly = !!mineOnlyDefault && !canSeeCompleteList && !embedded && !crmTicketsLayout && !portalView;
  const showScopeFilter = !embedded && !stateListLayout && (crmTicketsLayout || portalView || !lockMineOnly);
  const completeListDenied = !portalView && !embedded && maskCode === CRM_COMM_MASK_COMPLETE_LIST && !canSeeCompleteList;
  const moduleAccessDenied = !portalView && !embedded && !hasCrmModuleAccess(user);
  const portalAccessDenied = portalView && !hasFpCrmAccess(user);
  const canSetStandardMask = !!user?.permissions?.includes('set standard mask');
  const dateFmt = getDateFormat?.(state?.module_settings) ?? 'dd.MM.yyyy';
  const dateTimeFmt = `${dateFmt} HH:mm`;

  useEffect(() => {
    previewRef.current = preview;
    timePeriodRef.current = timePeriod;
    dateRef.current = date;
    scopeRef.current = scope;
  }, [preview, timePeriod, date, scope]);

  useEffect(() => {
    companyIdRef.current = companyId;
  }, [companyId]);

  useEffect(() => {
    traderBusinessScopeRef.current = !!traderBusinessScope;
  }, [traderBusinessScope]);
  useEffect(() => {
    filterTraderIdRef.current = filterTraderId;
  }, [filterTraderId]);
  useEffect(() => {
    typesRef.current = types;
  }, [types]);

  const mergeColumnVisibility = useCallback(
    (saved) => {
      const merged = mergeCrmCommColumnVisibility(saved, columnLayoutMode, stateListLayout);
      // Parita ServiceTaskList / SupportTicketList – stĺpec zmazania nie je v mask dialógu, nesmie zmiznúť.
      if (!stateListLayout && columnLayoutMode === 'crm_tickets' && canDeleteInTable) {
        merged.custom_delete = true;
      }
      return merged;
    },
    [canDeleteInTable, columnLayoutMode, stateListLayout],
  );

  const pageSizePc = useMemo(() => {
    const v = (state.module_settings || []).find((s) => s.module === 'global' && s.key === 'page_size_pc')?.value;
    const n = parseInt(v, 10);
    return Number.isFinite(n) && n > 0 ? n : 50;
  }, [state.module_settings]);

  const getTable = useCallback(() => tableRef.current?.table ?? tableRef.current, []);
  const captureHScroll = useCallback(() => {
    const table = getTable();
    if (!table) return;
    const holder = table.rowManager?.element;
    if (!holder) return;
    savedHScrollRef.current = { left: holder.scrollLeft, pending: true };
  }, [getTable]);
  const restoreHScroll = useCallback(() => {
    if (!savedHScrollRef.current.pending) return;
    const table = getTable();
    if (!table) return;
    const holder = table.rowManager?.element;
    const header = table.columnManager?.contentsElement;
    if (!holder) return;
    const left = savedHScrollRef.current.left;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        holder.scrollLeft = left;
        if (header) header.scrollLeft = left;
        savedHScrollRef.current.pending = false;
      });
    });
  }, [getTable]);
  // Soft refresh avoids Tabulator "Data Load Response Blocked" (no setData([]) while ajax active).
  const refreshFromStart = useCallback(
    () => softRefreshProgressiveFromStart(getTable, completedCacheRef, t('Common.LoadingData')),
    [getTable, t],
  );
  const drilldownOverallActive = hasDrilldownOverallStatusFilter(initialRequestFilters);

  const isTableVisible = useCallback(() => {
    const table = getTable();
    if (!table) return false;
    const el = table.getElement?.() ?? table.element ?? table.rowManager?.element?.parentElement;
    return !!(el && el.offsetParent != null && el.clientHeight > 0);
  }, [getTable]);

  const scheduleRedraw = useCallback(() => {
    if (rafRedrawRef.current != null) return;
    rafRedrawRef.current = requestAnimationFrame(() => {
      rafRedrawRef.current = null;
      const table = getTable();
      if (!table) return;
      pendingRowIdsRef.current.clear();
      needsRedrawRef.current = false;
      table.redraw?.(true);
    });
  }, [getTable]);

  const captureScrollForPusher = useCallback(() => {
    const table = getTable();
    if (!table?.rowManager?.element) return null;
    const holder = table.rowManager.element;
    const header = table.columnManager?.contentsElement;
    return { v: holder.scrollTop, h: header ? header.scrollLeft : holder.scrollLeft || 0 };
  }, [getTable]);

  const restoreScrollForPusher = useCallback((pos) => {
    if (!pos) return;
    requestAnimationFrame(() => {
      const table = getTable();
      if (!table?.rowManager?.element) return;
      const holder = table.rowManager.element;
      const header = table.columnManager?.contentsElement;
      holder.scrollTop = pos.v;
      if (header) header.scrollLeft = pos.h;
    });
  }, [getTable]);

  const applyTicketListRowFromServer = useCallback((ticketId, { fallbackRefresh = true } = {}) => {
    if (isRestoringRef.current) return;
    const table = getTable();
    if (!table?.getRow || !tableBuiltRef.current) {
      pendingPusherRefreshRef.current = true;
      return;
    }
    const scroll = captureScrollForPusher();
    const portalQs = portalView ? '?portal_view=1' : '';
    getRequest(`/api/crm_communication_tickets/${ticketId}/list_row${portalQs}`)
      .then((res) => {
        const row = res?.data;
        if (!row?.id) {
          if (fallbackRefresh) {
            captureHScroll();
            refreshFromStart();
          }
          return;
        }
        const table = getTable();
        const rc = table?.getRow?.(row.id);
        if (rc) {
          rc.update(row).catch(() => {});
        } else {
          table?.addData?.([row]);
        }
        pendingRowIdsRef.current.add(row.id);
        if (!isTableVisible()) needsRedrawRef.current = true;
        else scheduleRedraw();
        restoreScrollForPusher(scroll);
      })
      .catch(() => {
        if (fallbackRefresh) {
          captureHScroll();
          refreshFromStart();
        }
      });
  }, [captureHScroll, captureScrollForPusher, isTableVisible, portalView, refreshFromStart, restoreScrollForPusher, scheduleRedraw]);

  useEffect(() => {
    if (!preview) return;
    const isPeriod = PREVIEW_PERIOD_SEGMENTS.includes(preview);
    const nextStatus = isPeriod ? CRM_COMM_TICKETS_STATUS_VALUES.ALL : defaultListStatus;
    const nextScope = previewScopeToListScope(scope);
    setStatusValue(nextStatus);
    setScopeValue(nextScope);
    setTypeValue('All');
    statusValueRef.current = nextStatus;
    scopeValueRef.current = nextScope;
    typeValueRef.current = 'All';
    resetToFirstPageRef.current = true;
    refreshFromStart();
  }, [defaultListStatus, preview, scope, refreshFromStart]);

  const textHeaderFilter = useCallback((headerValue, rowValue) => {
    if (!headerValue) return true;
    const normalize = (str) =>
      String(str ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
    return normalize(rowValue).includes(normalize(headerValue));
  }, []);

  const columnsRef = useRef([]);

  const timezone = useMemo(
    () => getTimezone(state.module_settings),
    [state.module_settings],
  );

  const dateColumnFilter = useMemo(
    () => (columnLayoutMode === 'crm_tickets' || columnLayoutMode === 'portal'
      ? createCrmCommDateOrUserFilter(dateFmt, dateTimeFmt)
      : createCrmCommDateColumnFilter(dateFmt, dateTimeFmt)),
    [columnLayoutMode, dateFmt, dateTimeFmt],
  );

  const toggleTicketRead = useCallback(async (ticketId, markAsUnread = false) => {
    if (!ticketId) return;
    try {
      const url = markAsUnread
        ? `/api/crm_communication_tickets/${ticketId}/mark-as-not-viewed`
        : `/api/crm_communication_tickets/${ticketId}/read`;
      await postRequest(url, {}, true);
      const table = getTable();
      const row = table?.getRow?.(ticketId)
        ?? table?.getRows?.().find((r) => Number(r.getData?.()?.id) === Number(ticketId));
      if (row) {
        const data = row.getData?.() || {};
        await row.update({ ...data, is_unread: !!markAsUnread });
      }
      dispatch({ type: 'updateCrmTicketViewed', payload: ticketId, value: !!markAsUnread });
    } catch (_) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 3500 });
    }
  }, [dispatch, getTable, t, toast]);

  const getMaskFieldLabel = useCallback(
    (field) => {
      const labelKeys = columnLayoutMode === 'crm_tickets'
        ? CRM_COMM_TICKETS_MASK_FIELD_LABEL_KEYS
        : CRM_COMM_MASK_FIELD_LABEL_KEYS;
      const key = labelKeys[field];
      if (key) return t(key);
      const col = columnsRef.current?.find((item) => item?.field === field);
      const raw = col?.title != null ? String(col.title).trim() : '';
      return raw || field;
    },
    [columnLayoutMode, t],
  );

  const openDetail = useCallback(
    (ticketId) => {
      const tabLabel = t('CrmCommunication.Tab.TicketDetail');
      dispatch({
        type: 'create_new_tab',
        name: tabLabel, translationKey: 'CrmCommunication.Tab.TicketDetail', translationParams: { id: ticketId }, legacyLabel: tabLabel,
        page: 'CrmCommunicationTicketDetail',
        props: { ticketId, portalView: !!portalView },
        mask: portalView ? CRM_COMM_MASK_PORTAL_DETAIL : CRM_COMM_MASK_DETAIL,
      });
    },
    [dispatch, portalView, t],
  );

  const deleteTicket = useCallback(async (id) => {
    if (!id) return;
    try {
      const res = await deleteRequest(`/api/crm_communication_tickets/${id}`);
      if (res?.status === 200) {
        toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), life: 3000, detail: t('ServiceTask.TicketDeleted') });
        const row = getTable()?.getRow?.(id);
        if (row) row.delete?.();
        else refreshFromStart();
      } else {
        throw new Error(String(res?.message || JSON.stringify(res?.errors || res)));
      }
    } catch (_) {
      toast?.current?.show({ severity: 'warn', summary: t('ServiceTask.Rejected'), detail: t('ServiceTask.TicketDeleteFailed'), life: 3000 });
    }
  }, [getTable, refreshFromStart, t, toast]);

  const confirmDeleteTicket = useCallback((id) => {
    confirmDialog({
      message: t('ServiceTask.ConfirmDeleteTicket'),
      header: t('ServiceTask.ConfirmDeleteTitle'),
      icon: 'pi pi-info-circle',
      acceptLabel: t('Common.Yes'),
      rejectLabel: t('Common.No'),
      accept: () => {
        void deleteTicket(id);
      },
      reject: () => {},
    });
  }, [deleteTicket, t]);

  const [partnerCompanies, setPartnerCompanies] = useState([]);

  useEffect(() => {
    if (!crmTicketsLayout && !portalView) {
      setPartnerCompanies([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await loadCompaniesForTicketCatalog(getRequest);
        if (!cancelled) {
          setPartnerCompanies(data);
        }
      } catch {
        if (!cancelled) setPartnerCompanies([]);
      }
    })();
    return () => { cancelled = true; };
  }, [crmTicketsLayout, portalView]);

  const partnerCompanyOptions = useMemo(
    () => (partnerCompanies || [])
      .map((c) => ({ label: c.name || `ID ${c.id}`, value: c.name || '' }))
      .filter((o) => o.value)
      .sort((a, b) => (a.label || '').localeCompare(b.label || '', 'sk')),
    [partnerCompanies],
  );

  const columns = useMemo(() => {
    const defs = buildCrmCommunicationTableColumns({
      t,
      portalView,
      layoutMode: columnLayoutMode,
      stateListLayout,
      onToggleTicketRead: (columnLayoutMode === 'crm_tickets' || portalView) ? toggleTicketRead : undefined,
      onOpenDetail: (columnLayoutMode === 'crm_tickets' || portalView) ? openDetail : undefined,
      onDelete: (columnLayoutMode === 'crm_tickets' && canDeleteInTable) ? confirmDeleteTicket : undefined,
      formatDate,
      dateFmt,
      dateTimeFmt,
      getUserInitials,
      getUserColor,
      textHeaderFilter,
      dateColumnFilter,
      partnerCompanyOptions,
      moduleSettings: state.module_settings,
      crmStates: state.crm_states,
      crmSteps: state.crm_steps,
      locale,
      timezone,
    });
    columnsRef.current = defs;
    return defs;
  }, [t, portalView, columnLayoutMode, stateListLayout, toggleTicketRead, openDetail, canDeleteInTable, confirmDeleteTicket, formatDate, dateFmt, dateTimeFmt, getUserInitials, getUserColor, textHeaderFilter, dateColumnFilter, partnerCompanyOptions, state.module_settings, state.crm_states, state.crm_steps, locale, timezone]);

  const maskDialogFields = useMemo(() => {
    const keys = Object.keys(defaultVisible);
    if (columnLayoutMode !== 'crm_tickets') return keys;
    const hidden = stateListLayout
      ? ['rowSelection', 'editor', 'status_indicator', 'custom_delete']
      : ['editor', 'status_indicator', 'custom_delete'];
    return keys.filter((field) => !hidden.includes(field));
  }, [columnLayoutMode, defaultVisible, stateListLayout]);

  const getSelectedRows = useCallback(() => getTable()?.getSelectedData?.() ?? [], [getTable]);

  const onStateListRowSelectionChanged = useCallback(() => {
    const count = getSelectedRows().length;
    setToolbarEditDisabled(!(canEditToolbar && count === 1));
    setToolbarDeleteDisabled(!(canDeleteToolbar && count === 1));
  }, [canDeleteToolbar, canEditToolbar, getSelectedRows]);

  const onToolbarEditSelected = useCallback(() => {
    const selected = getSelectedRows();
    if (selected.length === 1) openDetail(selected[0].id);
  }, [getSelectedRows, openDetail]);

  const onToolbarDeleteSelected = useCallback(() => {
    const selected = getSelectedRows();
    if (selected.length === 1) confirmDeleteTicket(selected[0].id);
  }, [confirmDeleteTicket, getSelectedRows]);

  useEffect(() => {
    statusValueRef.current = statusValue;
  }, [statusValue]);
  useEffect(() => {
    scopeValueRef.current = scopeValue;
    mineOnlyRef.current = lockMineOnly || scopeValue === CRM_COMM_TICKETS_SCOPE_VALUES.MINE;
  }, [scopeValue, lockMineOnly]);
  useEffect(() => {
    typeValueRef.current = typeValue;
  }, [typeValue]);

  useEffect(() => {
    if (!topFiltersMountedRef.current) {
      topFiltersMountedRef.current = true;
      return;
    }
    if (isRestoringRef.current) return;
    captureHScroll();
    resetToFirstPageRef.current = true;
    const table = getTable();
    if (table?.rowManager?.element) {
      table.rowManager.element.scrollTop = 0;
      const header = table.columnManager?.contentsElement;
      if (header) header.scrollLeft = 0;
    }
    refreshFromStart();
  }, [statusValue, scopeValue, typeValue, captureHScroll, getTable, refreshFromStart]);

  /** Parametre z uloženej masky – prvý request ich použije, aby sa neposlali nefiltrované dáta pred tableBuilt. */
  const initialMaskParams = useMemo(() => {
    if (preview) return {};
    const effective = getCrmCommEffectiveMask(listPageKey, state, crmTicketsLayout);
    if (!effective || effective.type !== 'user' || !effective.userMask) return {};
    const parsedF = safeParse(effective.userMask.filters, {});
    const rawFilters = parsedF && typeof parsedF === 'object' && !Array.isArray(parsedF) ? parsedF : {};
    const out = {};
    const drilldownOwnsOverall = hasDrilldownOverallStatusFilter(initialRequestFilters);
    // Drilldown overall_status MUST survive mask restore (BE ignores overall when filter_status set).
    if (rawFilters.statusValue && !drilldownOwnsOverall) {
      const statusFromMask = crmTicketsLayout
        ? normalizeCrmCommTicketsStatusFromMask(rawFilters.statusValue)
        : rawFilters.statusValue;
      const apiStatus = mapCrmCommTicketsStatusToFilterStatus(statusFromMask);
      if (apiStatus) out.filter_status = apiStatus;
    }
    if (!lockMineOnly && rawFilters.scopeValue) {
      const scopeFromMask = crmTicketsLayout
        ? normalizeCrmCommTicketsScopeFromMask(rawFilters.scopeValue)
        : rawFilters.scopeValue;
      applyCrmCommTicketsScopeToRequestExtra(out, scopeFromMask, { lockMineOnly });
    }
    if (rawFilters.typeValue && rawFilters.typeValue !== 'All') {
      out.filter_type = rawFilters.typeValue;
    }
    const skip = new Set(['statusValue', 'scopeValue', 'typeValue']);
    const dateFilterFields = ['created_at', 'last_activity_at', 'waiting_for_action_by'];
    let hasDateFilter = false;
    Object.entries(rawFilters).forEach(([field, value]) => {
      if (skip.has(field)) return;
      if (value == null || String(value).trim() === '') return;
      // workflow_state_label maps to filter_status and would kill drilldown overall_status.
      if (drilldownOwnsOverall && (field === 'workflow_state_label' || field === 'overall_status')) return;
      appendCrmCommunicationTicketFilter({ field, value }, out);
      if (dateFilterFields.includes(field)) hasDateFilter = true;
    });
    if (hasDateFilter) out.filter_date_format = dateFmt;
    const sorters = parseSorters(effective.userMask.sorters);
    if (sorters.length) {
      out.sorters = JSON.stringify(sorters.map((s) => ({ field: s.field || s.column, dir: s.dir })));
    }
    return out;
  }, [crmTicketsLayout, dateFmt, initialRequestFilters, listPageKey, lockMineOnly, preview, state.user_mask_filters]);

  const buildRequestParams = useCallback(
    (paramsFromTabulator = {}) => {
      const extra = { ...extractSafePaginationParams(paramsFromTabulator) };
      if (resetToFirstPageRef.current) {
        extra.page = 1;
        resetToFirstPageRef.current = false;
      }
      if (extra.size == null) extra.size = pageSizePc;

      const p = preview ?? previewRef.current;
      const sc = scope ?? scopeRef.current;
      if (p) {
        const tp = timePeriod ?? timePeriodRef.current;
        const dt = date ?? dateRef.current;
        const { from, to } = getPreviewDateRange(tp, dt);
        extra.preview = p;
        if (sc) extra.scope = sc;
        if (from) extra.preview_time_from = from;
        if (to) extra.preview_time_to = to;
        extra.filter_status = CRM_COMM_TICKETS_STATUS_VALUES.ALL;
        if (crmTicketsLayout) {
          applyCrmCommTicketsScopeToRequestExtra(extra, previewScopeToListScope(sc), { lockMineOnly: false });
        } else {
          extra.mine_only = sc === 'moje_tikety' ? 1 : 0;
        }
        return extra;
      }

      if (crmTicketsLayout) {
        applyCrmCommTicketsScopeToRequestExtra(extra, scopeValueRef.current, { lockMineOnly });
      } else {
        extra.mine_only = (lockMineOnly || scopeValueRef.current === CRM_COMM_TICKETS_SCOPE_VALUES.MINE) ? 1 : 0;
      }
      if (portalView) extra.portal_view = 1;
      if (companyIdRef.current) extra.company_id = Number(companyIdRef.current);
      if (typesRef.current) extra.types = typesRef.current;
      if (traderBusinessScopeRef.current) extra.trader_business_scope = 1;
      if (filterTraderIdRef.current) extra.filter_trader_id = Number(filterTraderIdRef.current);
      const status = statusValueRef.current;
      const apiStatus = mapCrmCommTicketsStatusToFilterStatus(status);
      if (apiStatus) extra.filter_status = apiStatus;
      const type = typeValueRef.current;
      if (type && type !== 'All') extra.filter_type = type;
      const tableReadyForParams = tableBuiltRef.current;
      mergePagedStandardListFiltersAndSorters(extra, {
        getTable,
        tableBuiltForParams: tableReadyForParams,
        userMaskFilters: state.user_mask_filters,
        maskPage: listPageKey,
        filterKeyStyle: 'literal_field',
        appendFilter: appendCrmCommunicationTicketFilter,
      });
      if (!tableReadyForParams) {
        Object.keys(initialMaskParams).forEach((k) => {
          if (extra[k] === undefined) extra[k] = initialMaskParams[k];
        });
      }
      if (initialRequestFilters && typeof initialRequestFilters === 'object') {
        Object.assign(extra, initialRequestFilters);
      }
      const hf = tableReadyForParams ? (getTable()?.getHeaderFilters?.() ?? []) : [];
      const dateFilterFields = ['created_at', 'last_activity_at', 'waiting_for_action_by'];
      if (
        hf.some((f) => dateFilterFields.includes(f?.field) && String(f?.value ?? '').trim() !== '')
        || (!tableReadyForParams && initialMaskParams.filter_date_format)
      ) {
        extra.filter_date_format = dateFmt;
      }
      // Toolbar status must not override drilldown overall_status (BE mutual exclusion).
      if (apiStatus && !hasDrilldownOverallStatusFilter(initialRequestFilters)) {
        extra.filter_status = apiStatus;
      }
      if (type && type !== 'All') extra.filter_type = type;
      if (crmTicketsLayout) {
        applyCrmCommTicketsScopeToRequestExtra(extra, scopeValueRef.current, { lockMineOnly });
      } else {
        extra.mine_only = (lockMineOnly || scopeValueRef.current === CRM_COMM_TICKETS_SCOPE_VALUES.MINE) ? 1 : 0;
      }
      applyDrilldownOverallStatusPrecedence(extra, initialRequestFilters);
      return extra;
    },
    [date, dateFmt, getTable, initialMaskParams, initialRequestFilters, listPageKey, lockMineOnly, pageSizePc, portalView, preview, scope, crmTicketsLayout, state.user_mask_filters, timePeriod],
  );

  const fetchPaged = useCallback(
    (url, merged) =>
      tabulatorGet(url, merged).then((r) => ({
        data: Array.isArray(r?.data) ? r.data : [],
        last_page: Math.max(1, Number(r?.last_page) || 1),
        total: Number(r?.total) || 0,
        current_page: Number(r?.current_page) || 1,
      })),
    [],
  );

  const ajaxRequestFunc = useMemo(
    () => createDedupedAjaxRequest(buildRequestParams, fetchPaged, inFlightRef, completedCacheRef, isRestoringRef, lastResponseRef),
    [buildRequestParams, fetchPaged],
  );

  const setMask = (field, value) => {
    if (!Object.prototype.hasOwnProperty.call(defaultVisible, field)) return;
    if (field === 'editor' || field === 'rowSelection') return;
    if (listFrozenFields.includes(field) && value === false) return;
    if (field === 'id' && value === false) return;
    const next = mergeColumnVisibility({ ...defaultVisible, ...columnsVisible, [field]: value });
    if (!Object.values(next).some(Boolean)) return;
    applyDataColumnVisibilityToTable(getTable(), next, columnLayoutMode, stateListLayout);
    setColumnsVisible(next);
  };

  const maskSelectAll = () => {
    const next = mergeColumnVisibility({ ...defaultVisible });
    Object.keys(next).forEach((k) => { next[k] = true; });
    if (columnLayoutMode === 'crm_tickets' && canDeleteInTable) next.custom_delete = true;
    if (stateListLayout) {
      next.custom_delete = false;
    }
    applyDataColumnVisibilityToTable(getTable(), next, columnLayoutMode, stateListLayout);
    setColumnsVisible(next);
  };

  const maskDeselectAll = () => {
    const next = { ...defaultVisible };
    Object.keys(next).forEach((k) => { next[k] = false; });
    next.ticket_code = true;
    listFrozenFields.forEach((f) => { next[f] = true; });
    if (columnLayoutMode === 'crm_tickets') {
      next.id = true;
      if (canDeleteInTable) next.custom_delete = true;
    }
    if (stateListLayout) {
      next.custom_delete = false;
    }
    applyDataColumnVisibilityToTable(getTable(), next, columnLayoutMode, stateListLayout);
    setColumnsVisible(mergeColumnVisibility(next));
  };

  const getCrmCommPrintColumnOrder = useCallback(() => {
    const technicalFields = new Set(['editor', 'rowSelection']);
    if (columnLayoutMode !== 'crm_tickets') {
      const table = getTable();
      return (table?.getColumns?.() ?? [])
        .map((col) => col?.getField?.() ?? col?.getDefinition?.()?.field)
        .filter((field) => field && !technicalFields.has(field) && columnsVisible[field] !== false);
    }
    const allowedColumns = stateListLayout
      ? CRM_COMM_TICKETS_PRINT_COLUMNS_ALLOWED.filter((field) => field !== 'type')
      : CRM_COMM_TICKETS_PRINT_COLUMNS_ALLOWED;
    const allowedSet = new Set(allowedColumns);
    const fallback = () => allowedColumns.filter(
      (f) => columnsVisible[f] !== false,
    );
    const table = getTable();
    if (!table?.getColumns) return fallback();
    const seen = new Set();
    const out = [];
    try {
      for (const col of table.getColumns()) {
        const field = col.getField?.() ?? col.getDefinition?.()?.field;
        if (!field || !allowedSet.has(field) || columnsVisible[field] === false) continue;
        if (seen.has(field)) continue;
        seen.add(field);
        out.push(field);
      }
    } catch (_) {
      return fallback();
    }
    return out.length > 0 ? out : fallback();
  }, [columnLayoutMode, columnsVisible, getTable, stateListLayout]);

  const buildExportSuffix = useCallback(
    (opts = {}) => {
      const extra = buildRequestParams();
      const printColumns = getCrmCommPrintColumnOrder();
      return buildExportQueryStringFromParams(extra, {
        includePrintColumns: true,
        printColumns,
        ...opts,
      });
    },
    [buildRequestParams, getCrmCommPrintColumnOrder],
  );

  const runPrint = useCallback(async () => {
    try {
      setPrintChoiceVisible(false);
      setDialogText?.(t('Common.PreparingPrint'));
      setVisible?.(true);
      const suffix = buildExportSuffix({ printLayout: printLayoutChoice, printOrientation: printOrientationChoice });
      const html = await fetchTextGet(`/api/crm_communication_tickets/export/print${suffix}`);
      if (!openPrintHtmlWindow(html)) {
        toast?.current?.show({ severity: 'warn', summary: t('Common.Print'), detail: t('Common.EnablePopups'), life: 3500 });
      }
    } catch (e) {
      if (String(e?.message || '').includes('(404)')) {
        const table = getTable();
        table?.print?.(false, true);
        toast?.current?.show({ severity: 'warn', summary: t('Common.Print'), detail: t('ServiceTask.PrintFallback'), life: 3500 });
      } else {
        toast?.current?.show({ severity: 'error', summary: t('Common.Print'), detail: e?.message || t('ServiceTask.PrintFailed'), life: 4000 });
      }
    } finally {
      setVisible?.(false);
    }
  }, [buildExportSuffix, getTable, printLayoutChoice, printOrientationChoice, setDialogText, setVisible, t, toast]);

  const runExport = useCallback(async () => {
    const titleKey = listPageKey === CRM_COMM_PAGE_COMPLETE_LIST
      ? EXPORT_DOCUMENT_TITLE_KEYS.CRM_COMMUNICATION_FULL_LIST
      : EXPORT_DOCUMENT_TITLE_KEYS.CRM_COMMUNICATION_MY_AGENDA;
    try {
      setDialogText?.(t('Common.PreparingExport'));
      setVisible?.(true);
      const suffix = buildExportSuffix();
      const defaultXlsx = xlsxDownloadFilename(t(titleKey));
      const { blob, filename } = await downloadBlobGet(`/api/crm_communication_tickets/export/xlsx${suffix}`, defaultXlsx);
      triggerBlobDownload(blob, filename || defaultXlsx);
    } catch (e) {
      if (String(e?.message || '').includes('(404)')) {
        const table = getTable();
        table?.download?.('xlsx', xlsxDownloadFilename(t(titleKey)), {
          sheetName: excelSheetTitleFromDocumentTitle(t(titleKey)),
        });
        toast?.current?.show({ severity: 'warn', summary: t('Common.Export'), detail: t('ServiceTask.ExportFallback'), life: 3500 });
      } else {
        toast?.current?.show({ severity: 'error', summary: t('Common.Export'), detail: e?.message || t('ServiceTask.ExportFailed'), life: 4000 });
      }
    } finally {
      setVisible?.(false);
    }
  }, [buildExportSuffix, getTable, listPageKey, setDialogText, setVisible, t, toast]);

  // Pusher – parita ServiceTaskList: GET list_row, update/add riadok, deferred redraw
  useEffect(() => {
    const channel = getPusherChannel();
    if (!channel) return;

    const getId = (data) => data?.id ?? data?.ticket_id ?? data?.ticket?.id ?? null;
    const DEDUPE_MS = 800;
    const lastHandled = { key: '', at: 0 };
    const shouldSkip = (eventType, id) => {
      if (id == null) return true;
      const key = `${eventType}-${id}`;
      const now = Date.now();
      if (lastHandled.key === key && now - lastHandled.at < DEDUPE_MS) return true;
      lastHandled.key = key;
      lastHandled.at = now;
      return false;
    };

    const onUpdated = (data) => {
      const id = getId(data);
      if (id == null || shouldSkip('updated', id)) return;
      applyTicketListRowFromServer(id);
    };

    const onCreated = (data) => {
      const id = getId(data);
      if (id == null || shouldSkip('created', id)) return;
      applyTicketListRowFromServer(id);
    };

    const onDeleted = (data) => {
      const id = getId(data);
      if (id == null || shouldSkip('deleted', id)) return;
      const table = getTable();
      const row = table?.getRows?.().find((r) => r.getData?.()?.id === id);
      if (row) row.delete();
    };

    channel.bind('crm_communication_ticket.updated', onUpdated);
    channel.bind('crm_communication_ticket.created', onCreated);
    channel.bind('crm_communication_ticket.deleted', onDeleted);
    return () => {
      channel.unbind('crm_communication_ticket.updated', onUpdated);
      channel.unbind('crm_communication_ticket.created', onCreated);
      channel.unbind('crm_communication_ticket.deleted', onDeleted);
    };
  }, [applyTicketListRowFromServer, getTable]);

  useEffect(() => {
    const update = state.lastCrmTicketViewedUpdate;
    if (!update?.ticketId) return;
    const table = getTable();
    if (!table?.getRows) return;
    const row = table.getRows().find((r) => Number(r.getData?.()?.id) === Number(update.ticketId));
    if (row) {
      const data = row.getData?.();
      if (data) row.update({ ...data, is_unread: !!update.value }).catch(() => {});
    }
  }, [getTable, state.lastCrmTicketViewedUpdate]);

  useEffect(() => {
    const table = typeof getTable === 'function' ? getTable() : null;
    if (!table?.getColumns) return;
    const columnTitleKeys = (crmTicketsLayout || portalView || columnLayoutMode === 'crm_tickets')
      ? CRM_COMM_TICKETS_MASK_FIELD_LABEL_KEYS
      : CRM_COMM_FULL_COLUMN_I18N_KEYS;
    const typeOptions = CRM_COMMUNICATION_TYPE_FILTER_VALUES.map((type) => ({
      label: t(crmCommunicationTypeLabelKey(type)),
      value: type,
    }));
    const statusOptions = buildCrmWorkflowStatusFilterOptions(state.crm_states || [], locale);
    const readOptions = [
      { label: t('CrmCommunication.List.Unread'), value: true },
      { label: t('CrmCommunication.List.ReadOk'), value: false },
    ];
    applyTabulatorRuntimeI18n(table, t, {
      columnTitleKeys,
      headerFilters: {
        type: { placeholder: t('Common.All'), options: typeOptions },
        status_id: { placeholder: t('Common.All'), options: statusOptions },
        is_unread: { placeholder: t('Common.All'), options: readOptions },
      },
      placeholder: t('Common.NoData'),
      dataLoaderLoading: t('Common.LoadingData'),
      reformatRows: true,
    });
  }, [getTable, locale, t, crmTicketsLayout, portalView, columnLayoutMode, state.crm_states]);

  const checkStatus = useCallback((value) => {
    statusValueRef.current = value;
    setStatusValue(value);
  }, []);

  const checkScope = useCallback((value) => {
    scopeValueRef.current = value;
    setScopeValue(value);
    mineOnlyRef.current = lockMineOnly || value === CRM_COMM_TICKETS_SCOPE_VALUES.MINE;
  }, [lockMineOnly]);

  const checkType = useCallback((value) => {
    typeValueRef.current = value;
    setTypeValue(value);
  }, []);

  const closeMenu = () => setExpandedKeys({});

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (overlayRef.current && !overlayRef.current.contains(event.target)) {
        setExpandedKeys({});
      }
    };
    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, []);

  const scrollToFirst = () => scrollToFirstUtil(getTable());
  const scrollUp = () => scrollUpUtil(getTable());
  const scrollDown = () => scrollDownUtil(getTable());
  const scrollToLast = () => scrollToLastUtil(getTable());

  const standardMaskRow = useMemo(
    () => (state.standard_mask_filters || []).find((m) => m.page === columnMaskPage) ?? null,
    [columnMaskPage, state.standard_mask_filters],
  );

  const hasApplicableStandardMask = useMemo(() => {
    if (!standardMaskRow) return false;
    try {
      const attributes = sanitizeTabulatorLayoutAttributes(
        typeof standardMaskRow.attributes === 'string'
          ? JSON.parse(standardMaskRow.attributes)
          : standardMaskRow.attributes,
      );
      return Array.isArray(attributes) && attributes.length > 1;
    } catch {
      return false;
    }
  }, [standardMaskRow]);

  const getStandardMaskFallback = useCallback(() => {
    const row = (state.standard_mask_filters || []).find((m) => m.page === columnMaskPage);
    if (!row) return null;
    const jsonColumns = safeParse(row.columns, {});
    const merged = mergeColumnVisibility(jsonColumns && typeof jsonColumns === 'object' ? jsonColumns : {});
    const attributes = sanitizeTabulatorLayoutAttributes(safeParse(row.attributes, []));
    if (!Object.values(merged).some(Boolean)) return null;
    return { columns: merged, attributes };
  }, [columnMaskPage, mergeColumnVisibility, state.standard_mask_filters]);

  const confirmApplyStandardLayoutToUserMask = () => {
    closeMenu();
    if (!standardMaskRow) {
      toast?.current?.show({ severity: 'warn', summary: t('Common.StandardMask'), detail: t('BusinessPartnerList.StandardMaskMissing'), life: 4000 });
      return;
    }
    let attributes = [];
    let cols = {};
    try {
      attributes = sanitizeTabulatorLayoutAttributes(
        typeof standardMaskRow.attributes === 'string'
          ? JSON.parse(standardMaskRow.attributes)
          : (standardMaskRow.attributes || []),
      );
      cols = typeof standardMaskRow.columns === 'string'
        ? JSON.parse(standardMaskRow.columns)
        : (standardMaskRow.columns || {});
    } catch {
      toast?.current?.show({ severity: 'warn', summary: t('ServiceTask.StandardMask'), detail: t('ServiceTask.StandardMaskInvalidData'), life: 4000 });
      return;
    }
    if (!Array.isArray(attributes) || attributes.length <= 1) {
      toast?.current?.show({ severity: 'warn', summary: t('ServiceTask.StandardMask'), detail: t('ServiceTask.StandardMaskInvalidLayout'), life: 4000 });
      return;
    }
    const colsForSave = mergeColumnVisibility({ ...(cols || {}), status_indicator: true });
    if (!Object.values(colsForSave).some(Boolean)) {
      toast?.current?.show({ severity: 'warn', summary: t('Common.StandardMask'), detail: t('BusinessPartnerList.StandardMaskMissing'), life: 4000 });
      return;
    }
    confirmDialog({
      message: t('ServiceTask.ApplyStandardMaskPrompt'),
      header: t('ServiceTask.StandardMask'),
      icon: 'pi pi-info-circle',
      acceptLabel: t('Common.Use'),
      rejectLabel: t('Common.Cancel'),
      accept: () => {
        setUserMaskFilter(columnMaskPage, 'all', colsForSave, null, attributes, null, () => {
          const table = getTable();
          if (!table) return;
          isRestoringRef.current = true;
          try {
            table.setColumnLayout?.(attributes);
            setColumnsVisible(colsForSave);
            applyDataColumnVisibilityToTable(table, colsForSave, columnLayoutMode, stateListLayout);
          } finally {
            setTimeout(() => {
              isRestoringRef.current = false;
              table.redraw?.(true);
              captureHScroll();
              refreshFromStart();
            }, 0);
          }
        });
      },
    });
  };

  const confirmDeleteStandardMask = () => {
    closeMenu();
    if (!standardMaskRow?.id) {
      toast?.current?.show({ severity: 'warn', summary: t('Common.StandardMask'), detail: t('BusinessPartnerList.StandardMaskMissing'), life: 4000 });
      return;
    }
    confirmDialog({
      message: t('ServiceTask.ConfirmDeleteRecord'),
      header: t('Util.StandardMask.ToastTitle'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: t('Common.Yes'),
      rejectLabel: t('Common.No'),
      accept: () => deleteStandardMaskFilter?.(standardMaskRow.id),
    });
  };

  const resetMask = async () => {
    closeMenu();
    await resetTabulatorMask({
      page: listPageKey,
      defaultColumnsVisible: defaultVisible,
      ref: tableRef,
      columns,
      setColumnsVisible: (v) => setColumnsVisible(mergeColumnVisibility(v)),
      getFallbackMask: getStandardMaskFallback,
      progressiveRefresh: {
        getTable,
        completedCacheRef,
        savedHScrollRef,
        loadingMessage: t('Common.LoadingData'),
        beforeRefresh: captureHScroll,
      },
    });
    checkStatus(defaultListStatus);
    checkScope(crmTicketsLayout ? CRM_COMM_TICKETS_SCOPE_VALUES.ALL : (mineOnlyDefault ? CRM_COMM_TICKETS_SCOPE_VALUES.MINE : CRM_COMM_TICKETS_SCOPE_VALUES.ALL));
    checkType('All');
  };

  const confirmSaveStandardMask = () => {
    confirmDialog({
      message: t('ServiceTask.SaveStandardMaskPrompt'),
      header: t('ServiceTask.SaveStandardMaskTitle'),
      icon: 'pi pi-info-circle',
      acceptLabel: t('Common.Save'),
      rejectLabel: t('Common.Cancel'),
      accept: () => {
        const table = getTable();
        const layout = getTabulatorLayoutForStandardMask(table);
        if (!layout) {
          toast?.current?.show({ severity: 'warn', summary: t('ServiceTask.Mask'), detail: t('ServiceTask.TableNotReady'), life: 3500 });
          return;
        }
        if (!Object.values(columnsVisible).some(Boolean)) {
          toast?.current?.show({ severity: 'warn', summary: t('ServiceTask.StandardMask'), detail: t('ServiceTask.AtLeastOneColumnVisible'), life: 3500 });
          return;
        }
        const colsForSave = mergeColumnVisibility({ ...columnsVisible, status_indicator: true });
        setStandardMaskFilter?.(columnMaskPage, colsForSave, layout.attributes);
      },
    });
  };

  const setCommSelects = () => {
    const headerFilters = getTable()?.getHeaderFilters?.() ?? [];
    const headerFilterObj = headerFilters.reduce((acc, f) => {
      if (f?.field) acc[f.field] = f.value;
      return acc;
    }, {});
    setUserMaskFilter(listPageKey, 'set_filters', null, {
      ...headerFilterObj,
      statusValue,
      scopeValue,
      typeValue,
    }, null);
    setExpandedKeys({});
  };

  const clearCommSelects = () => {
    checkStatus(defaultListStatus);
    checkScope(crmTicketsLayout ? CRM_COMM_TICKETS_SCOPE_VALUES.ALL : (mineOnlyDefault ? CRM_COMM_TICKETS_SCOPE_VALUES.MINE : CRM_COMM_TICKETS_SCOPE_VALUES.ALL));
    checkType('All');
    getTable()?.clearHeaderFilter?.();
    setUserMaskFilter(listPageKey, 'set_filters', null, {}, null);
    setExpandedKeys({});
  };

  const saveUserMaskColumns = () => {
    const table = getTable();
    const headerFilters = table?.getHeaderFilters?.() ?? [];
    const headerFilterObj = headerFilters.reduce((acc, f) => {
      if (f?.field) acc[f.field] = f.value;
      return acc;
    }, {});
    const colsForSave = mergeColumnVisibility(columnsVisible);
    if (columnLayoutMode === 'crm_tickets' && canDeleteInTable) colsForSave.custom_delete = true;
    setUserMaskFilter(columnMaskPage, 'set_columns', colsForSave, null, table?.getColumnLayout?.() ?? null);
    setUserMaskFilter(listPageKey, 'set_filters', null, {
      ...headerFilterObj,
      statusValue,
      scopeValue,
      typeValue,
    }, null);
    setMaskVisible(false);
  };

  const itemRenderer = (item, options) => (
    <a className="flex align-items-center px-3 py-2 cursor-pointer items-center bg-[#333] text-white w-[180px]" onClick={options.onClick}>
      <span className={`${item.icon} text-primary`} />
      <span className={`mx-2 ${item.items && 'font-semibold'}`}>{item.label}</span>
      {item.shortcut && <span className="ml-auto border-1 surface-border border-round surface-100 text-xs p-1">{item.shortcut}</span>}
    </a>
  );

  const items = [{
    key: '0',
    label: t('Common.Actions'),
    icon: 'pi pi-sliders-v',
    template: itemRenderer,
    items: [
      { key: '0_1', label: t('Common.SetFilter'), icon: 'pi pi-filter', command: () => { setCommSelects(); }, template: itemRenderer },
      { key: '0_2', label: t('Common.ClearFilter'), icon: 'pi pi-filter-slash', command: () => { clearCommSelects(); }, template: itemRenderer },
      { key: '0_3', label: t('Common.SetSorting'), icon: 'pi pi-sort-alt', command: () => {
        setUserMaskFilter(listPageKey, 'set_sorters', null, null, null, normalizeCrmTicketSorters(cleanSorters(getTable()?.getSorters?.() ?? [])));
        setExpandedKeys({});
      }, template: itemRenderer },
      { key: '0_4', label: t('Common.ClearSorting'), icon: 'pi pi-sort-alt-slash', command: () => {
        setUserMaskFilter(listPageKey, 'set_sorters', null, null, null, []);
        getTable()?.setSort?.([]);
        setExpandedKeys({});
      }, template: itemRenderer },
      { key: '0_5', label: t('Common.SetMask'), icon: 'pi pi-server', command: () => { setMaskVisible(true); setExpandedKeys({}); }, template: itemRenderer },
      { key: '0_5s', label: t('Common.StandardMask'), icon: 'pi pi-clone', command: () => { confirmApplyStandardLayoutToUserMask(); setExpandedKeys({}); }, template: itemRenderer },
      ...(canSetStandardMask ? [
        { key: '0_5b', label: t('Common.SaveStandardMask'), icon: 'pi pi-bookmark', command: () => { confirmSaveStandardMask(); setExpandedKeys({}); }, template: itemRenderer },
        ...(hasApplicableStandardMask ? [{
          key: '0_5d',
          label: `${t('Common.Delete')} ${t('Common.StandardMask')}`,
          icon: 'pi pi-trash',
          command: () => { confirmDeleteStandardMask(); setExpandedKeys({}); },
          template: itemRenderer,
        }] : []),
      ] : []),
      { key: '0_5r', label: t('Common.ResetUserMask'), icon: 'pi pi-refresh', command: () => { resetMask(); setExpandedKeys({}); }, template: itemRenderer },
      { key: '0_6', label: t('Common.Notifications'), icon: 'pi pi-envelope', command: () => { openChosenNotificationPreferences?.(listPageKey); setExpandedKeys({}); }, template: itemRenderer },
    ],
  }];

  const tableHeightClass = embeddedFillParent
    ? 'h-full'
    : (embedded
      ? 'h-[min(360px,50vh)]'
      : (stateListLayout ? 'h-[calc(100vh_-_193px)]' : 'h-[calc(100vh_-_313px)]'));

  const applyTopFiltersFromMask = useCallback((table, rawFilters) => {
    const raw = rawFilters && typeof rawFilters === 'object' && !Array.isArray(rawFilters) ? rawFilters : {};
    // Drilldown overall_status owns the status universe for this tab — keep toolbar at ALL.
    if (drilldownOverallActive) {
      checkStatus(CRM_COMM_TICKETS_STATUS_VALUES.ALL);
    } else if (raw.statusValue) {
      checkStatus(crmTicketsLayout ? normalizeCrmCommTicketsStatusFromMask(raw.statusValue) : raw.statusValue);
    } else {
      checkStatus(defaultListStatus);
    }
    if (!lockMineOnly && raw.scopeValue) {
      checkScope(crmTicketsLayout ? normalizeCrmCommTicketsScopeFromMask(raw.scopeValue) : raw.scopeValue);
    } else {
      checkScope(crmTicketsLayout ? CRM_COMM_TICKETS_SCOPE_VALUES.ALL : (mineOnlyDefault ? CRM_COMM_TICKETS_SCOPE_VALUES.MINE : CRM_COMM_TICKETS_SCOPE_VALUES.ALL));
    }
    if (raw.typeValue) checkType(raw.typeValue);
    else checkType('All');
    const skip = new Set(['statusValue', 'scopeValue', 'typeValue']);
    Object.entries(raw).forEach(([field, value]) => {
      if (skip.has(field)) return;
      if (drilldownOverallActive && (field === 'workflow_state_label' || field === 'overall_status')) return;
      table?.setHeaderFilterValue?.(field, value ?? '', true);
    });
  }, [checkScope, checkStatus, checkType, defaultListStatus, drilldownOverallActive, lockMineOnly, mineOnlyDefault, crmTicketsLayout]);

  const persistLayoutEvents = useMemo(() => {
    const base = getTabulatorPersistLayoutEvents(columnMaskPage, tableRef, setUserMaskFilter);
    return {
      ...base,
      columnMoved: () => {
        if (!userInteractedRef.current || isRestoringRef.current) return;
        base.columnMoved?.();
      },
      columnResized: () => {
        if (!userInteractedRef.current || isRestoringRef.current) return;
        base.columnResized?.();
      },
    };
  }, [columnMaskPage, setUserMaskFilter]);

  const applyInitialHeaderFilters = useCallback((table) => {
    if (!table || !Array.isArray(initialHeaderFilters) || initialHeaderFilters.length === 0) return;
    isRestoringRef.current = true;
    try {
      initialHeaderFilters.forEach(({ field, value }) => {
        if (!field) return;
        table.setHeaderFilterValue?.(field, value);
      });
    } finally {
      setTimeout(() => { isRestoringRef.current = false; }, 0);
    }
  }, [initialHeaderFilters]);

  const tableEvents = useMemo(() => ({
    headerFilterChanged: () => {
      // Never refresh while painting mask/drilldown filters — avoids dual ajax + Data Load Response Blocked.
      if (isRestoringRef.current) return;
      userInteractedRef.current = true;
      captureHScroll();
      refreshFromStart();
    },
    sortChanged: () => {
      if (!isRestoringRef.current) userInteractedRef.current = true;
      captureHScroll();
    },
    dataSorted: () => {
      if (!isRestoringRef.current) userInteractedRef.current = true;
    },
    dataChanged: () => {
      if (isRestoringRef.current) return;
      checkStatus(statusValueRef.current);
      checkScope(scopeValueRef.current);
      checkType(typeValueRef.current);
    },
    tableBuilt: () => {
      const table = getTable();
      if (!table) return;
      tableBuiltRef.current = true;
      if (pendingPusherRefreshRef.current) {
        pendingPusherRefreshRef.current = false;
        captureHScroll();
        refreshFromStart();
      }
      const p = preview ?? previewRef.current;
      if (p) {
        const isPeriod = PREVIEW_PERIOD_SEGMENTS.includes(p);
        const nextStatus = isPeriod ? CRM_COMM_TICKETS_STATUS_VALUES.ALL : defaultListStatus;
        const nextScope = previewScopeToListScope(scope ?? scopeRef.current);
        checkStatus(nextStatus);
        checkScope(nextScope);
        checkType('All');
        setTimeout(() => {
          table?.clearHeaderFilter?.();
          refreshFromStart();
        }, 0);
        return;
      }
      const effective = getCrmCommEffectiveMask(listPageKey, state, crmTicketsLayout);
      isRestoringRef.current = true;
      userInteractedRef.current = false;
      try {
        if (effective?.type === 'user' && effective.userMask) {
          const parsedFilters = safeParse(effective.userMask.filters, {});
          const rawFilters = parsedFilters && typeof parsedFilters === 'object' && !Array.isArray(parsedFilters) ? parsedFilters : {};
          table.blockRedraw?.();
          try {
            applyUserMaskRowToTabulator(table, effective.userMask, {
              applyFilters: Array.isArray(parsedFilters) && parsedFilters.length > 0,
              setColumnsVisible: (v) => setColumnsVisible(mergeColumnVisibility(v)),
              clearHeaderFiltersWhenEmpty: true,
              applyColumnVisibilityFn: (tbl, jc, setVis) => {
                const merged = mergeColumnVisibility(jc);
                setVis(merged);
                applyDataColumnVisibilityToTable(tbl, merged, columnLayoutMode, stateListLayout);
              },
            });
            if (!Array.isArray(parsedFilters) || parsedFilters.length === 0) {
              applyTopFiltersFromMask(table, rawFilters);
            } else if (drilldownOverallActive) {
              checkStatus(CRM_COMM_TICKETS_STATUS_VALUES.ALL);
            }
          } finally {
            table.restoreRedraw?.(true);
          }
        } else if (effective?.type === 'standard' && effective.standardMask) {
          const attributes = sanitizeTabulatorLayoutAttributes(safeParse(effective.standardMask.attributes, []));
          const jsonColumns = safeParse(effective.standardMask.columns, {});
          if (Array.isArray(attributes) && attributes.length > 1) {
            try { table.setColumnLayout?.(attributes); } catch (_) {}
          }
          if (jsonColumns && typeof jsonColumns === 'object') {
            const merged = mergeColumnVisibility(jsonColumns);
            setColumnsVisible(merged);
            applyDataColumnVisibilityToTable(table, merged, columnLayoutMode, stateListLayout);
          }
          if (drilldownOverallActive) checkStatus(CRM_COMM_TICKETS_STATUS_VALUES.ALL);
        } else {
          applyDataColumnVisibilityToTable(table, defaultVisible, columnLayoutMode, stateListLayout);
          checkStatus(drilldownOverallActive ? CRM_COMM_TICKETS_STATUS_VALUES.ALL : defaultListStatus);
          checkScope(crmTicketsLayout ? CRM_COMM_TICKETS_SCOPE_VALUES.ALL : (mineOnlyDefault ? CRM_COMM_TICKETS_SCOPE_VALUES.MINE : CRM_COMM_TICKETS_SCOPE_VALUES.ALL));
          checkType('All');
        }
        applyInitialHeaderFilters(table);
      } finally {
        // Keep restore flag until after paint microtask so headerFilterChanged stays silent.
        setTimeout(() => {
          isRestoringRef.current = false;
        }, 0);
      }
      // First progressive ajax already used initialMaskParams + initialRequestFilters.
      // Do NOT fire a second replaceData/setData on open (single initial request contract).
    },
    ...persistLayoutEvents,
    renderComplete: () => restoreHScroll(),
    rowDblClick: (_e, row) => {
      const d = row.getData();
      openDetail(d.id, d.title, d.ticket_code);
    },
    ...(stateListLayout ? { rowSelectionChanged: onStateListRowSelectionChanged } : {}),
  }), [applyInitialHeaderFilters, applyTopFiltersFromMask, captureHScroll, checkScope, checkStatus, checkType, columnLayoutMode, defaultListStatus, defaultVisible, drilldownOverallActive, getTable, listPageKey, mergeColumnVisibility, mineOnlyDefault, onStateListRowSelectionChanged, openDetail, persistLayoutEvents, preview, refreshFromStart, restoreHScroll, scope, crmTicketsLayout, state, stateListLayout]);

  useEffect(() => {
    if (!needsRedrawRef.current || !isTableVisible()) return;
    scheduleRedraw();
  }, [isTableVisible, scheduleRedraw]);

  const tableOptions = useMemo(
    () => buildCrmCommunicationTabulatorOptions({
      t,
      embedded,
      embeddedFillParent,
      stateListLayout,
      listTitle,
      pageSizePc,
      ajaxRequestFunc,
      toast,
      branding: state?.branding,
      companySettingsPrint: state?.company_settings_print,
      moduleSettings: state?.module_settings,
      onAjaxRequesting: () => {
        if (tableBuiltRef.current && !isRestoringRef.current) captureHScroll();
        return true;
      },
      getTable,
      onAjaxResponse: (_url, _params, response) => {
        getTable()?.clearAlert?.();
        if (response && typeof response === 'object') lastResponseRef.current = response;
        return response;
      },
      onAjaxError: () => {
        getTable()?.clearAlert?.();
        toast?.current?.show({ severity: 'error', summary: t('Common.LoadFailed'), life: 4000 });
      },
    }),
    [ajaxRequestFunc, captureHScroll, embedded, embeddedFillParent, getTable, listTitle, pageSizePc, state?.branding, state?.company_settings_print, state?.module_settings, stateListLayout, t, toast],
  );

  // Locale must NOT remount the progressive table (display-only i18n via applyTabulatorRuntimeI18n).
  const tabulatorKey = `crm-comm-${listPageKey}-${columnLayoutMode}`;

  const selectClass = 'bg-[#333] text-white px-4 h-11 rounded-md';

  const statusFilterControl = crmTicketsLayout ? (
    <>
      <label className="font-semibold">{t('ServiceTask.Filter.Status')}:</label>
      <select className={selectClass} value={statusValue} onChange={(e) => checkStatus(e.target.value)}>
        <option value={CRM_COMM_TICKETS_STATUS_VALUES.OPEN}>{t('ServiceTask.Status.Open')}</option>
        <option value={CRM_COMM_TICKETS_STATUS_VALUES.CLOSED}>{t('ServiceTask.Status.Closed')}</option>
        <option value={CRM_COMM_TICKETS_STATUS_VALUES.REJECTED}>{t('ServiceTask.Status.Rejected')}</option>
        <option value={CRM_COMM_TICKETS_STATUS_VALUES.CANCELLED}>{t('ServiceTask.Status.Cancelled')}</option>
        <option value={CRM_COMM_TICKETS_STATUS_VALUES.ALL}>{t('Common.All')}</option>
      </select>
    </>
  ) : (
    <>
      <label className="font-semibold">{t('CrmCommunication.List.OverallStatus')}:</label>
      <select className={selectClass} value={statusValue} onChange={(e) => checkStatus(e.target.value)}>
        <option value="open">{t('ServiceTask.Status.Open')}</option>
        <option value="closed">{t('ServiceTask.Status.Closed')}</option>
        <option value="Všetky">{t('Common.All')}</option>
      </select>
    </>
  );

  const scopeFilterControl = showScopeFilter ? (
    <select className={selectClass} value={scopeValue} onChange={(e) => checkScope(e.target.value)}>
      <option value={CRM_COMM_TICKETS_SCOPE_VALUES.ALL}>{t('ServiceTask.Filter.AllTickets')}</option>
      <option value={CRM_COMM_TICKETS_SCOPE_VALUES.MINE}>{t('ServiceTask.Filter.CreatedByMe')}</option>
      <option value={CRM_COMM_TICKETS_SCOPE_VALUES.MENTIONED}>{t('ServiceTask.Filter.Mentioned')}</option>
      <option value={CRM_COMM_TICKETS_SCOPE_VALUES.EXECUTOR}>{t('ServiceTask.Filter.Executor')}</option>
    </select>
  ) : null;

  const typeFilterControl = !types ? (
    <>
      <label className="font-semibold">{t(crmTicketsLayout ? 'ServiceTask.TicketType' : 'CrmCommunication.List.Type')}:</label>
      <select className={selectClass} value={typeValue} onChange={(e) => checkType(e.target.value)}>
        <option value="All">{t('Common.All')}</option>
        {CRM_COMMUNICATION_TYPE_FILTER_VALUES.map((type) => (
          <option key={type} value={type}>{t(crmCommunicationTypeLabelKey(type))}</option>
        ))}
      </select>
    </>
  ) : null;

  const toolbarButtons = (
    <div className="bg-[#333] text-white rounded-md h-11 px-2 inline-flex items-center gap-1">
      <button type="button" className="h-9 w-9 inline-flex items-center justify-center rounded hover:bg-[#444] focus:outline-none" onClick={() => setPrintChoiceVisible(true)} title={t('Common.Print')}><PrintIcon className="shrink-0" /></button>
      <button type="button" className="h-9 w-9 inline-flex items-center justify-center rounded hover:bg-[#444] focus:outline-none" onClick={runExport} title={t('Common.ExportToExcel')}><ExportIcon className="shrink-0" /></button>
      <button type="button" className="h-9 w-9 inline-flex items-center justify-center rounded hover:bg-[#444] focus:outline-none" onClick={() => { userInteractedRef.current = true; captureHScroll(); refreshFromStart(); }} title={t('Common.Refresh')}><RefreshIcon className="shrink-0" /></button>
      <div className="h-9 inline-flex items-center text-white/70"><VerticalLineIcon /></div>
      <button type="button" className="h-9 w-9 inline-flex items-center justify-center rounded hover:bg-[#444] focus:outline-none" onClick={scrollToFirst} title={t('Icon.First')}><BackToFirstIcon /></button>
      <button type="button" className="h-9 w-9 inline-flex items-center justify-center rounded hover:bg-[#444] focus:outline-none" onClick={scrollUp} title={t('Icon.Previous')}><BackIcon /></button>
      <button type="button" className="h-9 w-9 inline-flex items-center justify-center rounded hover:bg-[#444] focus:outline-none" onClick={scrollDown} title={t('Icon.Next')}><ForwardIcon /></button>
      <button type="button" className="h-9 w-9 inline-flex items-center justify-center rounded hover:bg-[#444] focus:outline-none" onClick={scrollToLast} title={t('Icon.Last')}><ForwardToLastIcon /></button>
      <div className="h-9 inline-flex items-center text-white/70"><VerticalLineIcon /></div>
      <button type="button" className="h-9 w-9 inline-flex items-center justify-center rounded hover:bg-[#444] focus:outline-none" onClick={() => openFavoriteDialog?.(listTitle, favoritePageKey)} title={t('Common.AddToFavorites')}><FavoriteIcon /></button>
      <button type="button" className="h-9 w-9 inline-flex items-center justify-center rounded hover:bg-[#444] focus:outline-none" onClick={() => setFastView?.(favoritePageKey, columnsVisible, columns)} title={t('Common.QuickView')}><PriceIcon /></button>
      {canSetStandardMask ? (
        <button type="button" className="h-9 w-9 inline-flex items-center justify-center rounded hover:bg-[#444] focus:outline-none" onClick={confirmSaveStandardMask} title={t('Common.SaveStandardMask')}>
          <i className="pi pi-bookmark text-white" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );

  const panelMenuBlock = (
    <div className="relative w-full md:w-auto">
      <div className="absolute z-50 top-0 left-0 bg-[#333] rounded-md shadow-lg overflow-hidden" ref={overlayRef}>
        <PanelMenu
          model={items}
          expandedKeys={expandedKeys}
          onExpandedKeysChange={setExpandedKeys}
          className="w-full bg-[#333] text-white"
        />
      </div>
    </div>
  );

  const tableElement = (
    <ReactTabulator
      key={tabulatorKey}
      onRef={(r) => { tableRef.current = r?.current ?? r; }}
      columns={columns}
      className={tableHeightClass}
      selectable={stateListLayout || undefined}
      selectableRows={stateListLayout || undefined}
      options={tableOptions}
      events={tableEvents}
    />
  );

  if (moduleAccessDenied) {
    return (
      <div className="relative flex flex-col bg-gray-100 w-full py-4 px-4">
        <Message severity="warn" text={t('CrmCommunication.List.ModuleAccessDenied')} />
      </div>
    );
  }

  if (portalAccessDenied) {
    return (
      <div className="relative flex flex-col bg-gray-100 w-full py-4 px-4">
        <Message severity="warn" text={t('CrmCommunication.List.PortalAccessDenied')} />
      </div>
    );
  }

  if (completeListDenied) {
    return (
      <div className="relative flex flex-col bg-gray-100 w-full py-4 px-4">
        <Message severity="warn" text={t('CrmCommunication.List.CompleteListDenied')} />
      </div>
    );
  }

  if (embedded) {
    return (
      <div className={`flex min-h-0 flex-col${embeddedFillParent ? ' h-full' : ''}`}>
        <div className="min-h-0 flex-1 overflow-hidden">
          <ReactTabulator key={tabulatorKey} onRef={(r) => { tableRef.current = r?.current ?? r; }} columns={columns} className={tableHeightClass} options={tableOptions} events={tableEvents} />
        </div>
        <PrintOptionsDialog visible={printChoiceVisible} onHide={() => setPrintChoiceVisible(false)} orientation={printOrientationChoice} onOrientationChange={setPrintOrientationChoice} layout={printLayoutChoice} onLayoutChange={setPrintLayoutChoice} onConfirm={runPrint} />
        <TabulatorMaskDialog visible={maskVisible} onHide={() => setMaskVisible(false)} title={t('Common.SetMask')} fields={maskDialogFields} checkedMap={columnsVisible} onToggleField={setMask} onSelectAll={maskSelectAll} onDeselectAll={maskDeselectAll} onSave={saveUserMaskColumns} selectAllLabel={t('Common.SelectAll')} deselectAllLabel={t('Common.DeselectAll')} saveLabel={t('Common.Save')} getFieldLabel={getMaskFieldLabel} inputIdPrefix="crm-comm-mask" />
      </div>
    );
  }

  const listDialogs = (
    <>
      <PrintOptionsDialog visible={printChoiceVisible} onHide={() => setPrintChoiceVisible(false)} orientation={printOrientationChoice} onOrientationChange={setPrintOrientationChoice} layout={printLayoutChoice} onLayoutChange={setPrintLayoutChoice} onConfirm={runPrint} />
      <TabulatorMaskDialog visible={maskVisible} onHide={() => setMaskVisible(false)} title={t('Common.SetMask')} fields={maskDialogFields} checkedMap={columnsVisible} onToggleField={setMask} onSelectAll={maskSelectAll} onDeselectAll={maskDeselectAll} onSave={saveUserMaskColumns} selectAllLabel={t('Common.SelectAll')} deselectAllLabel={t('Common.DeselectAll')} saveLabel={t('Common.Save')} getFieldLabel={getMaskFieldLabel} inputIdPrefix="crm-comm-mask" />
      <span className="sr-only" data-page-key={listPageKey} data-mask={maskCode} />
    </>
  );

  if (crmTicketsLayout && stateListLayout) {
    return (
      <>
        <div className="h-full min-h-0 flex flex-col overflow-hidden">
          <div className="w-full h-10 flex flex-row bg-[#333] shrink-0">
            <IntersectionObserverWrapper>
              {canEditToolbar ? (
                <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="edit">
                  <button
                    type="button"
                    title={t('Icon.Edit')}
                    className="disabled:opacity-25"
                    disabled={toolbarEditDisabled}
                    onClick={onToolbarEditSelected}
                  >
                    <EditIcon />
                  </button>
                </div>
              ) : null}
              {canDeleteToolbar ? (
                <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="delete">
                  <button
                    type="button"
                    title={t('Common.Delete')}
                    className="disabled:opacity-25"
                    disabled={toolbarDeleteDisabled}
                    onClick={onToolbarDeleteSelected}
                  >
                    <DeleteIcon />
                  </button>
                </div>
              ) : null}
              <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="print">
                <button type="button" title={t('Common.Print')} onClick={() => setPrintChoiceVisible(true)}><PrintIcon /></button>
              </div>
              <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="export">
                <button type="button" title={t('Common.ExportToExcel')} onClick={runExport}><ExportIcon /></button>
              </div>
              <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="refresh">
                <button type="button" title={t('Common.Refresh')} onClick={() => { userInteractedRef.current = true; captureHScroll(); refreshFromStart(); }}><RefreshIcon /></button>
              </div>
              <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><VerticalLineIcon /></div>
              <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="first">
                <button type="button" title={t('Icon.First')} onClick={scrollToFirst}><BackToFirstIcon /></button>
              </div>
              <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="back">
                <button type="button" title={t('Icon.Previous')} onClick={scrollUp}><BackIcon /></button>
              </div>
              <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="forward">
                <button type="button" title={t('Icon.Next')} onClick={scrollDown}><ForwardIcon /></button>
              </div>
              <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="last">
                <button type="button" title={t('Icon.Last')} onClick={scrollToLast}><ForwardToLastIcon /></button>
              </div>
              <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><VerticalLineIcon /></div>
              <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="favorite">
                <button type="button" title={t('Common.AddToFavorites')} onClick={() => openFavoriteDialog?.(listTitle, favoritePageKey)}><FavoriteIcon /></button>
              </div>
              <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="fast">
                <button type="button" title={t('Common.QuickView')} onClick={() => setFastView?.(favoritePageKey, columnsVisible, columns)}><PriceIcon /></button>
              </div>
              {canSetStandardMask ? (
                <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="save-standard-mask">
                  <button type="button" title={t('Common.SaveStandardMask')} aria-label={t('Common.SaveStandardMask')} onClick={confirmSaveStandardMask} className="flex items-center justify-center">
                    <i className="pi pi-bookmark text-white text-xl" aria-hidden="true" />
                  </button>
                </div>
              ) : null}
              <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><VerticalLineIcon /></div>
              <div className="card flex justify-content-center z-[100]" data-targetid="actions" ref={overlayRef}>
                <PanelMenu model={items} expandedKeys={expandedKeys} onExpandedKeysChange={setExpandedKeys} className="w-full md:w-20rem" />
              </div>
            </IntersectionObserverWrapper>
          </div>
          <div className="px-1 bg-white rounded-md shadow-md">
            <div className="flex flex-col overflow-x-auto no-scrollbar min-h-[calc(100vh-177px)] min-w-0">
              <div className="overflow-hidden min-w-0">
                <div className="inline-block w-full py-2" ref={tableContainerRef}>
                  {tableElement}
                </div>
              </div>
            </div>
          </div>
        </div>
        {listDialogs}
      </>
    );
  }

  if (crmTicketsLayout) {
    return (
      <>
        <CrmCommunicationListPageShell
          title={listTitle}
          filterRow={(
            <>
              {statusFilterControl}
              {scopeFilterControl}
              {typeFilterControl}
            </>
          )}
          toolbar={toolbarButtons}
          panelMenu={panelMenuBlock}
          tableContainerRef={tableContainerRef}
        >
          {tableElement}
        </CrmCommunicationListPageShell>
        {listDialogs}
      </>
    );
  }

  return (
    <>
      <div className="relative flex flex-col bg-gray-100 w-full h-[calc(100vh_-_137px)] py-4 px-4 overflow-y-auto">
        <div className="w-full px-4 py-4 bg-[#333] text-white border rounded-sm">
          <h2 className="text-left text-xl font-semibold whitespace-nowrap">{listTitle}</h2>
        </div>
        <div className="flex flex-row flex-wrap items-start bg-white shadow-md p-4 gap-4">
          <div className="flex flex-row flex-wrap items-center gap-2">
            {statusFilterControl}
            {scopeFilterControl}
            {typeFilterControl}
          </div>
          <div className="bg-[#333] text-white rounded-md h-11 px-2 inline-flex items-center gap-1">
            <button type="button" className="h-9 w-9 inline-flex items-center justify-center rounded hover:bg-[#444] focus:outline-none" onClick={() => setPrintChoiceVisible(true)} title={t('Common.Print')}><PrintIcon className="shrink-0" /></button>
            <button type="button" className="h-9 w-9 inline-flex items-center justify-center rounded hover:bg-[#444] focus:outline-none" onClick={runExport} title={t('Common.ExportToExcel')}><ExportIcon className="shrink-0" /></button>
            <button type="button" className="h-9 w-9 inline-flex items-center justify-center rounded hover:bg-[#444] focus:outline-none" onClick={() => { userInteractedRef.current = true; captureHScroll(); refreshFromStart(); }} title={t('Common.Refresh')}><RefreshIcon className="shrink-0" /></button>
            <div className="h-9 inline-flex items-center text-white/70"><VerticalLineIcon /></div>
            <button type="button" className="h-9 w-9 inline-flex items-center justify-center rounded hover:bg-[#444] focus:outline-none" onClick={scrollToFirst} title={t('Icon.First')}><BackToFirstIcon /></button>
            <button type="button" className="h-9 w-9 inline-flex items-center justify-center rounded hover:bg-[#444] focus:outline-none" onClick={scrollUp} title={t('Icon.Previous')}><BackIcon /></button>
            <button type="button" className="h-9 w-9 inline-flex items-center justify-center rounded hover:bg-[#444] focus:outline-none" onClick={scrollDown} title={t('Icon.Next')}><ForwardIcon /></button>
            <button type="button" className="h-9 w-9 inline-flex items-center justify-center rounded hover:bg-[#444] focus:outline-none" onClick={scrollToLast} title={t('Icon.Last')}><ForwardToLastIcon /></button>
            <div className="h-9 inline-flex items-center text-white/70"><VerticalLineIcon /></div>
            <button type="button" className="h-9 w-9 inline-flex items-center justify-center rounded hover:bg-[#444] focus:outline-none" onClick={() => openFavoriteDialog?.(listTitle, favoritePageKey)} title={t('Common.AddToFavorites')}><FavoriteIcon /></button>
            <button type="button" className="h-9 w-9 inline-flex items-center justify-center rounded hover:bg-[#444] focus:outline-none" onClick={() => setFastView?.(favoritePageKey, columnsVisible, columns)} title={t('Common.QuickView')}><PriceIcon /></button>
            {canSetStandardMask ? (
              <button type="button" className="h-9 w-9 inline-flex items-center justify-center rounded hover:bg-[#444] focus:outline-none" onClick={confirmSaveStandardMask} title={t('Common.SaveStandardMask')}>
                <i className="pi pi-bookmark text-white" aria-hidden="true" />
              </button>
            ) : null}
          </div>
          <div className="relative w-full md:w-auto">
            <div className="absolute z-50 top-0 left-0 bg-[#333] rounded-md shadow-lg overflow-hidden" ref={overlayRef}>
              <PanelMenu
                model={items}
                expandedKeys={expandedKeys}
                onExpandedKeysChange={setExpandedKeys}
                className="w-full bg-[#333] text-white"
              />
            </div>
          </div>
        </div>
        <div ref={tableContainerRef}>
          <ReactTabulator key={tabulatorKey} onRef={(r) => { tableRef.current = r?.current ?? r; }} columns={columns} className={tableHeightClass} options={tableOptions} events={tableEvents} />
        </div>
      </div>
      <PrintOptionsDialog visible={printChoiceVisible} onHide={() => setPrintChoiceVisible(false)} orientation={printOrientationChoice} onOrientationChange={setPrintOrientationChoice} layout={printLayoutChoice} onLayoutChange={setPrintLayoutChoice} onConfirm={runPrint} />
      <TabulatorMaskDialog visible={maskVisible} onHide={() => setMaskVisible(false)} title={t('Common.SetMask')} fields={maskDialogFields} checkedMap={columnsVisible} onToggleField={setMask} onSelectAll={maskSelectAll} onDeselectAll={maskDeselectAll} onSave={saveUserMaskColumns} selectAllLabel={t('Common.SelectAll')} deselectAllLabel={t('Common.DeselectAll')} saveLabel={t('Common.Save')} getFieldLabel={getMaskFieldLabel} inputIdPrefix="crm-comm-mask" />
      <span className="sr-only" data-page-key={listPageKey} data-mask={maskCode} />
    </>
  );
}
